import { ReactNode, useEffect } from 'react';
import BlogNav from './BlogNav';
import StickyCtaBar from './StickyCtaBar';

interface BlogLayoutProps {
  children: ReactNode;
  title: string;
  description: string;
  keywords: string;
  canonicalPath: string;
  datePublished: string;
  dateModified: string;
  schemaMarkup?: string;
}

export default function BlogLayout({
  children,
  title,
  description,
  keywords,
  canonicalPath,
  datePublished,
  dateModified,
  schemaMarkup,
}: BlogLayoutProps) {
  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    setMeta('keywords', keywords);
    setMeta('author', 'Mason Hale');
    setMeta('robots', 'index, follow');

    const canonical = `https://masonhalefit.com${canonicalPath}`;
    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute('href', canonical);

    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:url', canonical, true);
    setMeta('og:type', 'article', true);
    setMeta('og:site_name', 'Mason Hale Fitness', true);
    setMeta('article:published_time', datePublished, true);
    setMeta('article:modified_time', dateModified, true);
    setMeta('article:author', 'Mason Hale', true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);

    let lastModEl = document.querySelector('meta[http-equiv="last-modified"]') as HTMLMetaElement | null;
    if (!lastModEl) {
      lastModEl = document.createElement('meta');
      lastModEl.setAttribute('http-equiv', 'last-modified');
      document.head.appendChild(lastModEl);
    }
    lastModEl.setAttribute('content', dateModified);

    if (schemaMarkup) {
      const existingScript = document.getElementById('blog-schema');
      if (existingScript) existingScript.remove();
      const script = document.createElement('script');
      script.id = 'blog-schema';
      script.type = 'application/ld+json';
      script.textContent = schemaMarkup;
      document.head.appendChild(script);
    }

    return () => {
      const s = document.getElementById('blog-schema');
      if (s) s.remove();
    };
  }, [title, description, keywords, canonicalPath, datePublished, dateModified, schemaMarkup]);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <BlogNav />
      <main className="pt-20 pb-24">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
          {children}
        </div>
      </main>
      <StickyCtaBar />
    </div>
  );
}
